import React, { useEffect } from 'react';

const Portfolio = () => {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-on-scroll');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const projects = [
    {
      title: "Luxury Managed Apartments",
      description: "Flagship residential project spanning 200,000+ sq. ft. with gross development value upwards of $40 million",
      image: "https://images.unsplash.com/photo-1773470920361-4f6cbb702ce0?w=800&q=80",
      category: "Residential Development"
    },
    {
      title: "Members-Only Sports & Activities Club",
      description: "State-of-the-art arena spanning 60,000 sq ft with professional facilities for 10+ sports",
      image: "https://images.unsplash.com/photo-1641352848874-c96659e03144?w=800&q=80",
      category: "Lifestyle Infrastructure"
    },
    {
      title: "Strategic Land Banking Portfolio",
      description: "200+ acres across multiple growth corridors with active capital rotation",
      image: "https://images.unsplash.com/photo-1695666403934-5929e4690900?w=800&q=80",
      category: "Land Acquisition"
    },
    {
      title: "Plotted Development Projects",
      description: "Multiple projects aggregating over 4 million sq.ft. of built real estate",
      image: "https://images.unsplash.com/photo-1574848296471-28f79a036f79?w=800&q=80",
      category: "Development"
    }
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="min-h-[60vh] flex items-center justify-center bg-[#F5F1ED] relative overflow-hidden">
        <div className="absolute right-20 top-1/4 w-96 h-96 border border-[#A08872]/10 rounded-full"></div>
        <div className="container mx-auto px-6 py-20 relative z-10">
          <p className="text-sm text-[#8B7355] uppercase tracking-widest mb-4 fade-on-scroll opacity-0">PORTFOLIO</p>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-serif text-[#2A2A2A] mb-6 fade-on-scroll opacity-0">Our Expertise</h1>
          <p className="text-lg text-[#3D3D3D] max-w-3xl fade-on-scroll opacity-0">
            Over 200+ acres controlled across multiple geographies, with total estimated asset valuation exceeding $100 million.
          </p>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="fade-on-scroll opacity-0 group cursor-pointer overflow-hidden">
                <div className="relative h-[280px] md:h-[500px] overflow-hidden mb-6">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2A2A2A]/80 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 p-8 text-white">
                    <p className="text-sm text-[#A08872] uppercase tracking-widest mb-2">{project.category}</p>
                    <h3 className="text-3xl font-serif mb-3">{project.title}</h3>
                    <p className="text-sm opacity-90">{project.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#EDE8E3]">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center fade-on-scroll opacity-0">
              <p className="text-5xl md:text-6xl font-serif text-[#8B7355] mb-4">200+</p>
              <p className="text-lg text-[#3D3D3D]">Acres Controlled</p>
            </div>
            <div className="text-center fade-on-scroll opacity-0">
              <p className="text-5xl md:text-6xl font-serif text-[#8B7355] mb-4">$100M+</p>
              <p className="text-lg text-[#3D3D3D]">Asset Valuation</p>
            </div>
            <div className="text-center fade-on-scroll opacity-0">
              <p className="text-5xl md:text-6xl font-serif text-[#8B7355] mb-4">4M+</p>
              <p className="text-lg text-[#3D3D3D]">Sq. Ft. Developed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Diversification */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-serif text-[#2A2A2A] mb-8 md:mb-12 fade-on-scroll opacity-0">Strategic Diversification</h2>
          
          <div className="space-y-8">
            <div className="border-l-4 border-[#8B7355] pl-8 fade-on-scroll opacity-0">
              <h3 className="text-2xl font-serif text-[#2A2A2A] mb-3">Mygen Equity Partners</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                The real estate private equity platform. Through this vehicle, UK Stage participates in land acquisition strategies, development-stage investments, repositioning opportunities, and joint venture structures.
              </p>
            </div>
            
            <div className="border-l-4 border-[#8B7355] pl-8 fade-on-scroll opacity-0">
              <h3 className="text-2xl font-serif text-[#2A2A2A] mb-3">UK Stage Music Label</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                Building original intellectual property and scalable creative assets. By combining capital with strategic oversight, the group strengthens cultural capital while creating monetizable content ecosystems.
              </p>
            </div>
            
            <div className="border-l-4 border-[#8B7355] pl-8 fade-on-scroll opacity-0">
              <h3 className="text-2xl font-serif text-[#2A2A2A] mb-3">Muzi Connect</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                A digital aggregation platform for artists, structuring collaboration, discovery, and monetization within the evolving creator economy.
              </p>
            </div>
            
            <div className="border-l-4 border-[#8B7355] pl-8 fade-on-scroll opacity-0">
              <h3 className="text-2xl font-serif text-[#2A2A2A] mb-3">SwasthiQ</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                An integrated health-tech ecosystem designed to unify fragmented medical data systems and create interoperable infrastructure across clinics, hospitals, and diagnostics.
              </p>
            </div>
            
            <div className="border-l-4 border-[#8B7355] pl-8 fade-on-scroll opacity-0">
              <h3 className="text-2xl font-serif text-[#2A2A2A] mb-3">Forms & Functions</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                An artificial intelligence-driven architecture initiative focused on generative design systems and structural optimization. This directly strengthens the group's real estate capabilities by integrating computational intelligence into development planning.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;